import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Award, BookOpen, CalendarDays, ChevronRight,
  Flame, Headphones, Languages, Loader2, Mic2, PenLine, RefreshCw,
  Target, Trophy, Zap,
} from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { dashboardAPI } from '../services/api';

const continueCourses = [
  { title: 'Leximi B2', category: 'LEXIM', description: 'Tekste dhe ushtrime sipas formatit Goethe', path: '/exam/b2/lesen', icon: BookOpen, tone: 'red' },
  { title: 'Dëgjimi B2', category: 'DËGJIM', description: 'Përmirëso kuptimin me audio autentike', path: '/exam/b2/hoeren', icon: Headphones, tone: 'gold' },
];

const recommendations = [
  { title: 'Fjalor', description: 'Mëso dhe përsërit fjalë të reja', path: '/vocabulary', icon: Languages, tone: 'red' },
  { title: 'Gramatikë', description: 'Përforco rregullat e gjermanishtes', path: '/grammar', icon: BookOpen, tone: 'dark' },
  { title: 'Të folur', description: 'Ushtrohu për pjesën Sprechen', path: '/exam/b2/sprechen', icon: Mic2, tone: 'gold' },
  { title: 'Shkrim', description: 'Shkruaj më qartë dhe me siguri', path: '/writing-journal', icon: PenLine, tone: 'dark' },
];

const displayNumber = (value) => Number(value || 0).toLocaleString('de-DE');

const DashboardPage = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    dashboardAPI.getStats()
      .then(({ data }) => { if (mounted) setStats(data); })
      .catch(() => { if (mounted) setError(t('Statistikat nuk mund të ngarkoheshin.')); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [t]);

  const xp = stats?.xp ?? user?.xp ?? 0;
  const streak = stats?.streak ?? user?.streak ?? 0;
  const totalWords = stats?.vocab?.total ?? 0;
  const knownWords = stats?.vocab?.known ?? 0;
  const dueWords = stats?.srs_due ?? 0;
  const vocabularyProgress = totalWords ? Math.min(100, Math.round((knownWords / totalWords) * 100)) : 0;
  const accuracy = stats?.successRate ?? 0;
  const level = user?.cefrLevel || user?.cefr_level || stats?.cefr_level || 'B2';
  const name = user?.firstName || user?.username || 'Student';
  const firstCourse = dueWords ? {
    title: t('Përsëritja e fjalëve'),
    category: t('FJALOR'),
    description: `${dueWords} ${t(dueWords === 1 ? 'fjalë pret' : 'fjalë presin')} ${t('për përsëritje')}`,
    path: '/unknown-words',
    icon: RefreshCw,
    tone: 'red',
  } : continueCourses[0];

  const weekDays = useMemo(() => {
    const today = new Date();
    const activityDates = new Set(stats?.activity_dates || []);
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (6 - index));
      const dateKey = date.toISOString().slice(0, 10);
      return {
        label: date.toLocaleDateString(language === 'de' ? 'de-DE' : 'sq-AL', { weekday: 'short' }).slice(0, 2),
        date: date.getDate(),
        active: activityDates.has(dateKey),
      };
    });
  }, [stats?.activity_dates, language]);

  return (
    <DashboardLayout>
      <main className="page-shell-wide min-h-full max-w-[1440px]">
        <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_350px]">
          <div className="min-w-0 space-y-8">
            <header className="flex flex-col justify-between gap-5 rounded-2xl border border-[#eee9e1] bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:p-7">
              <div className="min-w-0">
                <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#dd0000]">{t('Paneli yt i mësimit')}</p>
                <h1 className="break-words text-2xl font-extrabold tracking-tight text-[#171717] sm:text-4xl">{t('Mirëdita, {name}!').replace('{name}', name)}</h1>
                <p className="mt-2 text-sm text-zinc-500">{t('Vazhdo rrugëtimin tënd nga A1 deri në C2.')}</p>
              </div>
              <div className="flex w-fit shrink-0 items-center gap-2 rounded-xl bg-[#fff8d9] px-3.5 py-2.5 text-sm font-bold text-zinc-700">
                <Award size={17} className="text-[#dd0000]" /> {t('Niveli')} {level}
              </div>
            </header>

            {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}

            <section>
              <div className="mb-4 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0"><h2 className="text-2xl font-extrabold text-[#171717]">{t('Vazhdo mësimin')}</h2><p className="mt-1 text-sm text-zinc-500">{t('Mësimi yt i radhës është vetëm një klikim larg.')}</p></div>
                <Link to="/exam/b2" className="inline-flex shrink-0 items-center gap-1 text-sm font-bold text-[#dd0000] hover:underline">{t('Shiko të gjitha')} <ChevronRight size={16} /></Link>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {[firstCourse, continueCourses[1]].map((course, index) => {
                  const Icon = course.icon;
                  return (
                    <Link key={`${course.path}-${index}`} to={course.path} className="group relative min-w-0 overflow-hidden rounded-2xl border border-[#e5e1da] bg-white p-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-[#dd0000]/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#dd0000] focus-visible:ring-offset-2 sm:p-5">
                      <div className={`absolute right-0 top-0 h-24 w-24 rounded-full blur-2xl ${course.tone === 'red' ? 'bg-[#dd0000]/10' : 'bg-[#ffce00]/20'}`} />
                      <div className="relative">
                        <div className="flex items-start justify-between">
                          <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${course.tone === 'red' ? 'bg-[#fff0f0] text-[#dd0000]' : 'bg-[#fff8d9] text-[#947600]'}`}><Icon size={21} /></span>
                          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-extrabold tracking-wider text-zinc-500">{t(course.category)}</span>
                        </div>
                        <h3 className="mt-7 text-xl font-extrabold text-[#171717]">{t(course.title)}</h3>
                        <p className="mt-1 min-h-10 text-sm leading-5 text-zinc-500">{t(course.description)}</p>
                        <div className="mt-5 flex items-center justify-between border-t border-[#e9e4dc] pt-4">
                          <span className="inline-flex items-center gap-2 text-sm font-extrabold text-[#dd0000]">{t('Fillo tani')} <ArrowRight size={15} className="transition group-hover:translate-x-1" /></span>
                          <span className="text-xs text-zinc-400">B2</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>

            <section>
              <div className="mb-4"><h2 className="text-2xl font-extrabold text-[#171717]">{t('Rekomanduar për ty')}</h2><p className="mt-1 text-sm text-zinc-500">{t('Zgjidh një aftësi për të ushtruar sot.')}</p></div>
              <div className="grid gap-3 sm:grid-cols-2">
                {recommendations.map(({ title, description, path, icon: Icon, tone }) => (
                  <Link key={title} to={path} className="group flex items-center gap-4 rounded-2xl border border-[#e3e3e3] bg-white p-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-[#dd0000]/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#dd0000] focus-visible:ring-offset-2">
                    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone === 'red' ? 'bg-[#fff0f0] text-[#dd0000]' : tone === 'gold' ? 'bg-[#fff8d9] text-[#8b7000]' : 'bg-[#f1f1f1] text-[#171717]'}`}><Icon size={21} /></span>
                    <span className="min-w-0 flex-1"><strong className="block text-sm font-extrabold text-[#171717]">{t(title)}</strong><span className="mt-1 block text-xs text-zinc-500">{t(description)}</span></span>
                    <ArrowRight size={16} className="text-zinc-300 transition group-hover:translate-x-1 group-hover:text-[#dd0000]" />
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="min-w-0 space-y-5">
            <section className="rounded-2xl border border-[#e3e3e3] bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div><p className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">{t('Përmbledhje')}</p><h2 className="mt-1 text-xl font-extrabold text-[#171717]">{t('Progresi yt')}</h2></div>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff0f0] text-[#dd0000]"><Target size={19} /></div>
              </div>
              <div className="mt-5 grid grid-cols-3 divide-x divide-[#ececec] text-center">
                <MiniStat icon={Flame} value={streak} label={t('Ditë rresht')} />
                <MiniStat icon={Zap} value={displayNumber(xp)} label={t('Pikë XP')} />
                <MiniStat icon={Trophy} value={`${accuracy}%`} label={t('Saktësi')} />
              </div>
            </section>

            <section className="rounded-2xl border border-[#e3e3e3] bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between"><div><p className="text-xs font-extrabold uppercase tracking-wider text-zinc-400">{t('Qëndrueshmëria')}</p><h2 className="mt-1 text-xl font-extrabold text-[#171717]">{t('Seria javore')}</h2></div><CalendarDays size={18} className="text-zinc-400" /></div>
              <div className="mt-5 flex items-end justify-between gap-1">
                {weekDays.map((day) => <div key={`${day.label}-${day.date}`} className="flex flex-1 flex-col items-center gap-2"><span className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-extrabold ${day.active ? 'bg-[#dd0000] text-white' : 'bg-[#f1f1f1] text-zinc-500'}`}>{day.date}</span><span className="text-[10px] font-bold uppercase text-zinc-400">{day.label}</span></div>)}
              </div>
              <p className="mt-5 rounded-xl bg-[#fff8d9] px-3 py-2.5 text-center text-xs font-bold text-[#806700]">{t(streak ? 'Shumë mirë! Mos e ndërprit ritmin.' : 'Fillo sot serinë tënde të mësimit.')}</p>
            </section>

            <section className="rounded-2xl border border-[#e3e3e3] bg-[#171717] p-5 text-white shadow-sm">
              <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ffce00] text-[#171717]"><Languages size={19} /></div><div><p className="text-xs font-bold text-zinc-400">{t('FJALORI')}</p><h2 className="font-extrabold">{t('Përforco fjalët')}</h2></div></div>
              <p className="mt-4 text-sm leading-5 text-zinc-300">{totalWords ? `${knownWords} ${t('nga')} ${totalWords} ${t('fjalë janë shënuar si të njohura.')}` : t('Ruaj fjalët e reja dhe ndërto fjalorin tënd.')}</p>
              {totalWords > 0 && (
                <div className="mt-4">
                  <div className="mb-1.5 flex justify-between text-[11px] font-bold text-zinc-400">
                    <span>{t('Fjalë të njohura')}</span>
                    <span>{vocabularyProgress}%</span>
                  </div>
                  <div
                    role="progressbar"
                    aria-label={t('Progresi i fjalorit')}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={vocabularyProgress}
                    className="h-2 overflow-hidden rounded-full bg-white/15"
                  >
                    <div className="h-full rounded-full bg-[#ffce00] transition-[width]" style={{ width: `${vocabularyProgress}%` }} />
                  </div>
                </div>
              )}
              <Link to={dueWords ? '/unknown-words' : '/vocabulary'} className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold text-[#ffce00] hover:underline">{dueWords ? `${t('Përsërit fjalë')} ${dueWords}` : t('Hap fjalorin')} <ArrowRight size={15} /></Link>
            </section>
          </aside>
        </div>
        {loading && <div role="status" className="fixed bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-[#171717] px-4 py-2 text-xs font-bold text-white shadow-xl"><Loader2 size={14} className="animate-spin" /> {t('Po ngarkohet')}</div>}
      </main>
    </DashboardLayout>
  );
};

const MiniStat = ({ icon: Icon, value, label }) => <div className="px-2"><Icon size={16} className="mx-auto text-[#dd0000]" /><strong className="mt-1 block text-lg font-extrabold text-[#171717]">{value}</strong><span className="block text-[10px] text-zinc-500">{label}</span></div>;

export default DashboardPage;
